/**
 * Helpers for deciding how a video should play, and whether its signed URL is
 * stale.
 *
 * Thinkific lesson videos are stored with `videoUrl` pointing at a Thinkific
 * "videoproxy" player page (an embeddable Wistia player) — NOT a raw media file.
 * Those must be shown in an <iframe>, and the URL is signed + time-limited
 * (`expiry` + `verify` query params), so a stored copy goes stale and must be
 * refreshed before playback.
 */

/**
 * A Thinkific signed player page we embed via <iframe> (not a <video> tag).
 * @param {string} videoUrl
 * @returns {boolean}
 */
export function isThinkificEmbed(videoUrl) {
  if (!videoUrl) return false;
  return videoUrl.includes('/videoproxy/') || videoUrl.includes('wistia');
}

/**
 * Read the `expiry` (unix seconds) from a Thinkific signed URL.
 * @param {string} videoUrl
 * @returns {number|null} expiry in seconds, or null if absent/unparseable
 */
export function getUrlExpiry(videoUrl) {
  if (!videoUrl) return null;
  try {
    const exp = new URL(videoUrl).searchParams.get('expiry');
    if (!exp) return null;
    const n = Number(exp);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

/**
 * True if a signed URL is expired or will expire within `bufferSec` seconds.
 * A URL with no `expiry` param is treated as stale (refresh to be safe).
 * @param {string} videoUrl
 * @param {number} [bufferSec=300] refresh this many seconds before actual expiry
 * @returns {boolean}
 */
export function isSignedUrlStale(videoUrl, bufferSec = 300) {
  const exp = getUrlExpiry(videoUrl);
  if (exp == null) return true;
  return exp <= (Date.now() / 1000) + bufferSec;
}
